import { Link } from "react-router-dom";
import { trackLandingEvent } from "../../lib/analytics";

/**
 * Minimal landing layout — no Navbar, AnnouncementBar, OrderOnBehalfBanner, or
 * full Footer.  Used by conversion-optimised landing pages (e.g. /lp/*) where
 * nothing should compete with the primary CTA.
 */
export default function LandingLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* ── Slim header ────────────────────────────────────────────── */}
      <header className="border-b border-gray-100 bg-white/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" aria-label="Premier Pharma home">
            <img
              src="/logo.svg"
              alt="Premier Pharma"
              className="h-8 w-auto"
            />
          </Link>

          {/* Single CTA */}
          <Link
            to="/register"
            onClick={() => trackLandingEvent("landing_page_cta_click", window.location.pathname, { buttonLabel: "Open Account", buttonLocation: "header" })}
            className="inline-flex items-center gap-1.5 bg-accent hover:bg-accent-500 text-white text-sm font-semibold px-4 py-2 rounded-btn transition-colors duration-200"
          >
            Open Account
          </Link>
        </div>
      </header>

      {/* ── Page content ───────────────────────────────────────────── */}
      <main className="flex-1">{children}</main>

      {/* ── Slim footer with trust badges ──────────────────────────── */}
      <footer className="border-t border-gray-100 bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            {/* Trust badge row */}
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-5">
              {/* NABP */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-gray-700">NABP VAWD Accredited</span>
              </div>

              {/* Account review */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-gray-700">Credential review required</span>
              </div>
            </div>

            <p className="text-xs text-gray-400 text-center sm:text-right">
              © {new Date().getFullYear()} Premier Pharma. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
